import React from "react";
import { APP_CONFIG, GOOGLE_MAP_MODE_NAMES } from "../../constants/constants";
import { TMapRef, TMapRefGeozones } from "../../pages/map-fleet/type";
import { TMapRef as TMapRefVehicle } from "../../pages/map-asset/type";
import MapZoomControl from "./zoomControl";
import GAutocompleteField from "../../pages/map-fleet/gAutocompleteField";
import svgIcons from "../../assets/svg/svgIcons";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import { TMapState, TLatLng } from "../../types/map";
import { mapGetCurrentPosition } from "../../utils/map";
import { useGMapMode } from "../../hooks/mapGoogle";
import { useMapInit } from "../../hooks/mapInit";
import { toast } from "react-toastify";
import { AdminFormFieldSubmit } from "../admin/formFields";
import ModalSendLocation from "../../pages/map-fleet/modalSendLocation";

type TDisplayMode = "full" | "satellite-with-zoom" | "only-zoom";

type MapTopRightControlsProps = {
  displayMode?: TDisplayMode;
  mapRef:
    | React.RefObject<TMapRefVehicle>
    | React.RefObject<TMapRef>
    | React.RefObject<TMapRefGeozones>;
  onGAutocompleteClearCallback?: () => void;
  onSearchboxToggle?: (isOpen: boolean) => void;
  onDirectionChange?: (renderer: google.maps.DirectionsRenderer | null) => void;
  clearSearchRef?: React.MutableRefObject<
    ((preserveZoom?: boolean) => void) | null
  >;
};

const MapTopRightControls: React.FC<MapTopRightControlsProps> = ({
  displayMode = "full",
  mapRef,
  onGAutocompleteClearCallback,
  onSearchboxToggle,
  onDirectionChange,
  clearSearchRef,
}) => {
  const dispatch = useDispatch();
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const { onGAutocompletePlaceChanged } = useMapInit();
  const { gMapIsInMode } = useGMapMode();

  // show address searchbox
  const [showAddressSearchbox, setShowAddressSearchbox] = React.useState(false);
  const [hasSearchMarker, setHasSearchMarker] = React.useState(false);

  // State to hold the directions renderer instance
  const [directionsRenderer, setDirectionsRenderer] =
    React.useState<google.maps.DirectionsRenderer | null>(null);

  React.useEffect(() => {
    const checkMarker = () => {
      setHasSearchMarker(!!(mapRef.current as any)?.searchMarker);
    };

    // Check immediately and set up interval for reactive updates
    checkMarker();
    const interval = setInterval(checkMarker, 500);

    return () => clearInterval(interval);
  }, [mapRef]);

  const [searchedLocation, setSearchedLocation] = React.useState({
    lat: 0,
    lng: 0,
  });

  const onGAutocompleteClear = (preserveZoom = false) => {
    if (APP_CONFIG.DEBUG.MAPS) console.log("onAutocompleteClear");
    const currentMapRef = mapRef.current as any;

    // Clear search marker
    if (currentMapRef?.searchMarker) {
      currentMapRef.searchMarker.setMap(null);
      currentMapRef.searchMarker = null;
      setHasSearchMarker(false);
    }

    // Clear directions route
    if (directionsRenderer) {
      directionsRenderer.setMap(null);
      setDirectionsRenderer(null);
      onDirectionChange?.(null);
    }

    setShowAddressSearchbox(false);
    onSearchboxToggle?.(false);

    if (!preserveZoom) {
      onGAutocompleteClearCallback?.();
    }
    setSearchedLocation({ lat: 0, lng: 0 });
  };

  React.useEffect(() => {
    if (clearSearchRef) {
      clearSearchRef.current = onGAutocompleteClear;
    }

    return () => {
      if (clearSearchRef) clearSearchRef.current = null;
    };
  }, [clearSearchRef, directionsRenderer]);

  const handleGetDirection = () => {
    const currentMapRef = mapRef.current as any;
    if (!currentMapRef?.searchMarker || !currentMapRef?.map) {
      console.error("Search marker or map not available.");
      return;
    }

    const destination = currentMapRef.searchMarker.getPosition();
    if (!destination) {
      console.error("Could not get destination position from marker.");
      return;
    }
    mapGetCurrentPosition((originLatLng: TLatLng) => {
      const origin = {
        lat: originLatLng.latitude,
        lng: originLatLng.longitude,
      };
      // Initialize Directions services
      const directionsService = new google.maps.DirectionsService();

      // Get or create a new DirectionsRenderer
      let renderer = directionsRenderer;
      if (!renderer) {
        renderer = new google.maps.DirectionsRenderer();
        setDirectionsRenderer(renderer);
      }

      // Ensure the renderer is attached to the map every time.
      renderer.setMap(currentMapRef.map);

      //  Create the request
      const request: google.maps.DirectionsRequest = {
        origin: origin,
        destination: destination,
        travelMode: google.maps.TravelMode.DRIVING,
      };
      //  Request the route
      directionsService.route(request, (result, status) => {
        console.log("status", status);
        if (status === google.maps.DirectionsStatus.OK) {
          //  Render the route
          renderer!.setDirections(result);
          onDirectionChange?.(renderer!);

          // Hide the search marker and box
          currentMapRef.searchMarker.setMap(null);
          setHasSearchMarker(false);
        } else {
          // console.log("result", result);
          // console.log("status", status);
          console.error(`Directions request failed due to ${status}`);
          toast.error(
            "There are no direct routes to reach the searched place from your location"
          );
          // If route fails, detach renderer from map
          renderer?.setMap(null);
        }
      });
    });
  };

  if (
    // display top right controls only when map is ready
    !mapState?.mapData?.ready &&
    !mapRef.current?.map
    // !mapState?.mapScriptLoaded || // this can be used instead of mapData?.ready for ease, otherwise sometimes the controls dont load
    // display top right controls only when streetview is not active
    // 11/13/2025 instead of returning null when in streetview, we hide the controls via invisible class to prevent resetting the searched location in the searchbox
    // || gMapIsInMode(mapRef.current?.map, GOOGLE_MAP_MODE_NAMES.STREETVIEW) === true
  )
    return null;

  const showLayersButton = displayMode !== "only-zoom";
  const showSearchbox = displayMode === "full" && showAddressSearchbox;

  return (
    <div
      className={`absolute flex gap-2${displayMode === "only-zoom" ? " -mt-2" : ""}${
        // in case of streetview, we hide the controls via invisible class to prevent resetting the searched location in the searchbox
        gMapIsInMode(mapRef.current?.map, GOOGLE_MAP_MODE_NAMES.STREETVIEW) ===
        true
          ? " invisible"
          : ""
      } top-5 right-5 z-[200]`}
      // z-[${APP_CONFIG.MAPS.CONTROL_BUTTONS_ZINDEX}]
    >
      {/* Search box and button */}
      {displayMode === "full" && (
        <>
          {showSearchbox ? (
            <div className="flex flex-col">
              <div className={"relative"}>
                <GAutocompleteField
                  onPlaceChanged={(place: google.maps.places.PlaceResult) =>
                    onGAutocompletePlaceChanged(
                      place,
                      mapRef as React.RefObject<{ map: any }>
                    )
                  }
                  onFieldClear={onGAutocompleteClear}
                />
              </div>
              {/* 
          <AdminFormFieldAsyncDropdown
            loadingData={isFetchingAutosuggest}
            label={""}
            id="search_address"
            name="search_address"
            noOptionsMessage={() =>
              t("search_address_no_items")
            }
            placeholder={t("search_address_placeholder")}
            loadOptions={
              debouncedLoadOptionsHandlerAutosuggest
            }
            onChange={handleAutosuggestChange}
            detailsFormField={true}
            customWrapperclassName="relative w-72"
            searchStyle={true}
            searchStyleOnClear={() => {
              setShowAddressSearchbox(false);
              // reset map to default view
              mapUpdatesHandler(
                getMapOpsProps(),
                "centerToPushpin",
                checkedVehicles
              );
            }}
            isClearable={true}
            key={`map_search_${mapSearchCountRef.current}`}
          />
          */}
            </div>
          ) : (
            <button
              className="bg-white hover:bg-gray-100 flex items-center justify-center w-8 h-8 rounded-md shadow-md"
              type="button"
              onClick={() => {
                setShowAddressSearchbox(true);
                onSearchboxToggle?.(true);
              }}
            >
              <img
                className="size-4 grayscale opacity-70"
                src={svgIcons.SearchIcon}
                alt=""
              />
            </button>
          )}
        </>
      )}
      {/* Get Directions Button - Only shown when search marker exists */}
      {displayMode === "full" && hasSearchMarker && (
        <AdminFormFieldSubmit
          buttonDimensionClass={`h-10 w-30 px-4`}
          customWrapperClass="absolute top-11 right-36 w-40"
          label={`Get Directions`}
          onClick={handleGetDirection}
          type={"button"}
          variant="primary"
        />
      )}
      {displayMode === "full" && hasSearchMarker && (
        <AdminFormFieldSubmit
          buttonDimensionClass={`h-10 w-30 px-4`}
          customWrapperClass="absolute top-11 right-2 w-40"
          label={`Send Location`}
          type="button"
          variant="primary"
          onClick={() => {
            const currentMapRef = mapRef.current as any;
            const position = currentMapRef?.searchMarker?.getPosition();
            if (position) {
              setSearchedLocation({
                lat: position.lat(),
                lng: position.lng(),
              });
            }
            dispatch(setModalsData({ ...modalsState, showSendLocation: true }));
          }}
        />
      )}
      {showLayersButton && (
        <button
          className={`bg-white hover:bg-gray-100 flex items-center justify-center w-8 h-8 rounded-md shadow-md`}
          type="button"
          onClick={() => {
            dispatch(
              setModalsData({
                ...modalsState,
                showGroupSelector: false,
                showVehicleFilter: false,
                showVehicleDetails: false,
                showLayerFilter: true,
              })
            );
            setShowAddressSearchbox(false);
          }}
        >
          <img
            className="size-[22px] grayscale opacity-70 translate-y-[1px]"
            src={svgIcons.MapLayersIcon}
            alt=""
          />
        </button>
      )}

      {/* Zoom / Unzoom Buttons */}
      <MapZoomControl mapRef={mapRef as React.RefObject<TMapRef>} />
      {/*Sending Searched location */}
      <ModalSendLocation
        latitude={searchedLocation.lat}
        longitude={searchedLocation.lng}
      />
    </div>
  );
};
export default MapTopRightControls;
